<template>
    <div class="enterprise-analytics-dashboard">
        <div class="dashboard-header">
            <div class="header-title">
                <h1>{{ dashboardTitle }}</h1>
                <span v-if="locationCount" class="location-count">
                    {{ locationCount }} {{ locationCount === 1 ? 'location' : 'locations' }}
                </span>
            </div>
            <DateRangeFilter v-model="dateRange" @update:modelValue="handleDateChange" />
        </div>

        <div v-if="loading && !summary" class="loading-state">
            <ArrowPathIcon class="spinner-icon" />
            <p>Loading analytics...</p>
        </div>

        <div v-else-if="error" class="error-state">
            <i class="fas fa-exclamation-circle"></i>
            <p>{{ error }}</p>
        </div>

        <div v-else-if="summary" class="dashboard-content">
            <ExecutiveSummaryCard :summary="summary" />

            <div class="insights-section">
                <div class="insights-grid">
                    <InsightCard title="Top Performers" subtitle="Locations with highest hire counts" :icon="TrophyIcon"
                        variant="success">
                        <div v-if="topPerformers.length > 0" class="insight-list">
                            <div v-for="(location, index) in topPerformers" :key="location.company_id"
                                class="insight-item">
                                <div class="insight-rank">{{ index + 1 }}</div>
                                <div class="insight-details">
                                    <div class="insight-name">
                                        <span v-if="location.city || location.state">
                                            {{ location.city }}{{ location.city && location.state ? ', ' : '' }}{{ location.state }}
                                        </span>
                                        <span v-else>—</span>
                                    </div>
                                    <div class="insight-region">{{ location.group_name }}</div>
                                </div>
                                <div class="insight-metric">
                                    <span class="metric-value">{{ location.hires?.total || 0 }}</span>
                                    <span class="metric-label">hires</span>
                                </div>
                            </div>
                        </div>
                        <div v-else class="insight-empty">
                            <i class="fas fa-info-circle"></i>
                            <p>No location data available</p>
                        </div>
                    </InsightCard>

                    <InsightCard title="Needs Attention" subtitle="Locations requiring review"
                        :icon="ExclamationTriangleIcon" variant="warning">
                        <div v-if="needsAttention.length > 0" class="insight-list">
                            <div v-for="location in needsAttention" :key="location.company_id"
                                class="insight-item attention-item">
                                <div class="insight-details">
                                    <div class="insight-name">
                                        <span v-if="location.city || location.state">
                                            {{ location.city }}{{ location.city && location.state ? ', ' : '' }}{{ location.state }}
                                        </span>
                                        <span v-else>—</span>
                                    </div>
                                    <div class="attention-reasons">
                                        <span v-if="(location.roles?.aging_candidates || 0) > 15"
                                            class="reason-badge aging">
                                            <i class="fas fa-clock"></i>
                                            {{ location.roles.aging_candidates }} aging
                                        </span>
                                        <span v-if="(location.interviews?.candidate_no_show_rate || 0) > 15"
                                            class="reason-badge no-show">
                                            <i class="fas fa-user-slash"></i>
                                            {{ location.interviews.candidate_no_show_rate.toFixed(1) }}% no-show
                                        </span>
                                        <span v-if="(location.hires?.avg_time_to_hire || 0) > 20"
                                            class="reason-badge slow">
                                            <i class="fas fa-hourglass-half"></i>
                                            {{ Math.round(location.hires.avg_time_to_hire) }}d to hire
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="insight-empty success">
                            <i class="fas fa-check-circle"></i>
                            <p>All locations performing well</p>
                        </div>
                    </InsightCard>

                    <InsightCard title="Regional Overview" subtitle="Performance by region" :icon="MapIcon"
                        variant="info">
                        <div v-if="regionSummary.length > 0" class="insight-list">
                            <div v-for="region in regionSummary" :key="region.name" class="insight-item region-item">
                                <div class="insight-details">
                                    <div class="insight-name">{{ region.name }}</div>
                                    <div class="region-stats">
                                        {{ region.locationCount }} {{ region.locationCount === 1 ? 'location' :
                                            'locations' }}
                                        • {{ region.totalHires }} hires
                                    </div>
                                </div>
                                <div class="insight-metric small">
                                    <span class="metric-value">{{ region.avgTimeToHire }}</span>
                                    <span class="metric-label">avg days</span>
                                </div>
                            </div>
                        </div>
                        <div v-else class="insight-empty">
                            <i class="fas fa-info-circle"></i>
                            <p>No regional data available</p>
                        </div>
                    </InsightCard>
                </div>
            </div>

            <div class="table-section">
                <LocationPerformanceTable :locations="locations" :loading="loading"
                    @open-roles-modal="openRolesModal" />
            </div>
        </div>

        <OpenRolesModal :is-open="isModalOpen" :company-id="selectedCompanyId" :company-name="selectedCompanyName"
            :city="selectedCity" :state="selectedState" @close="closeRolesModal" />
    </div>
</template>

<script setup>

import { ref, computed, onMounted } from "vue";
import { UseEnterpriseAnalytics } from "../composables/UseEnterpriseAnalytics";
import { useAccountStore } from "../stores/AccountStore";
import ExecutiveSummaryCard from "../components/analytics/ExecutiveSummaryCard.vue";
import InsightCard from "../components/analytics/InsightCard.vue";
import LocationPerformanceTable from "../components/analytics/LocationPerformanceTable.vue";
import OpenRolesModal from "../components/analytics/OpenRolesModal.vue";
import DateRangeFilter from "../components/analytics/DateRangeFilter.vue";
import { TrophyIcon, ExclamationTriangleIcon, MapIcon, ArrowPathIcon } from "@heroicons/vue/24/outline";

const {
  loading,
  error,
  summary,
  locations,
  dateRange,
  topPerformers,
  needsAttention,
  regionSummary,
  fetchSummary,
  fetchLocations,
} = UseEnterpriseAnalytics();

const accountStore = useAccountStore();

const isFranchisor = computed(() => {
  return accountStore.user?.activeEnterprise?.isFranchisor === true;
});

const dashboardTitle = computed(() => {
  return isFranchisor.value ? "Enterprise Analytics" : "My Locations Analytics";
});

const locationCount = computed(() => {
  return locations.value?.length || 0;
});

const isModalOpen = ref(false);
const selectedCompanyId = ref(null);
const selectedCompanyName = ref("");
const selectedCity = ref(null);
const selectedState = ref(null);

function openRolesModal({ companyId, companyName, city, state }) {
  selectedCompanyId.value = companyId;
  selectedCompanyName.value = companyName;
  selectedCity.value = city;
  selectedState.value = state;
  isModalOpen.value = true;
}

function closeRolesModal() {
  isModalOpen.value = false;
}

onMounted(async () => {
  await fetchSummary();
  await fetchLocations();
});

function handleDateChange() {
  fetchSummary();
  fetchLocations();
}

</script>

<style scoped>
.enterprise-analytics-dashboard {
    padding: 2rem;
    max-width: 1600px;
    margin: 0 auto;
}

.dashboard-header h1 {
    margin: 0;
    font-size: 1.875rem;
    font-weight: 700;
    color: #ffffff;
}

.header-title {
    display: flex;
    align-items: baseline;
    gap: 1rem;
}

.location-count {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 500;
}

.dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
}

.loading-state,
.error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
}

.spinner-icon {
    width: 48px;
    height: 48px;
    color: #3b82f6;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
}

.loading-state p {
    color: #6b7280;
    margin: 0;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.error-state i {
    font-size: 3rem;
    color: #ef4444;
    margin-bottom: 1rem;
}

.error-state p {
    color: #6b7280;
}

.dashboard-content {
    display: flex;
    flex-direction: column;
    gap: 2rem;
}

.insights-section {
    width: 100%;
}

.insights-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 1.5rem;
}

.insight-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    max-height: 400px;
    overflow-y: auto;
    padding-right: 0.5rem;
}

.insight-list::-webkit-scrollbar {
    width: 6px;
}

.insight-list::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 3px;
}

.insight-list::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
}

.insight-list::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
}

.insight-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    background-color: #f9fafb;
    border-radius: 0.5rem;
    transition: background-color 0.15s;
}

.insight-item:hover {
    background-color: #f3f4f6;
}

.insight-rank {
    flex-shrink: 0;
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border-radius: 0.375rem;
    font-weight: 700;
    font-size: 0.875rem;
}

.insight-details {
    flex: 1;
    min-width: 0;
}

.insight-name {
    font-weight: 600;
    color: #111827;
    font-size: 0.875rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.insight-region {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.125rem;
}

.region-stats {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.125rem;
}

.insight-metric {
    flex-shrink: 0;
    text-align: right;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

.insight-metric.small {
    min-width: 60px;
}

.metric-value {
    font-weight: 700;
    color: #111827;
    font-size: 1.125rem;
    line-height: 1;
}

.metric-label {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.125rem;
}

.insight-empty {
    text-align: center;
    padding: 2rem 1rem;
    color: #9ca3af;
}

.insight-empty i {
    font-size: 2rem;
    margin-bottom: 0.5rem;
    display: block;
}

.insight-empty p {
    margin: 0;
    font-size: 0.875rem;
}

.insight-empty.success {
    color: #22c55e;
}

.attention-item {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
}

.attention-reasons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.reason-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.625rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    font-weight: 500;
}

.reason-badge i {
    font-size: 0.625rem;
}

.reason-badge.aging {
    background-color: #fef3c7;
    color: #92400e;
}

.reason-badge.no-show {
    background-color: #fee2e2;
    color: #991b1b;
}

.reason-badge.slow {
    background-color: #fef3c7;
    color: #92400e;
}

.region-item .insight-details {
    flex: 1;
}

.table-section {
    width: 100%;
}

@media (max-width: 1024px) {
    .insights-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 640px) {
    .enterprise-analytics-dashboard {
        padding: 1rem;
    }

    .dashboard-header {
        flex-direction: column;
        align-items: stretch;
        gap: 1rem;
    }

    .dashboard-content {
        gap: 1.5rem;
    }
}
</style>